import React, { useState, useEffect } from 'react';
import api, { API_BASE } from '../../api/client';
import { Plus, Trash2, Edit3, Image, CheckCircle, AlertCircle, GripVertical, ExternalLink } from 'lucide-react';

function Notification({ message, type, onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
    const colors = type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 'bg-red-50 border-red-400 text-red-800';
    return (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg border-l-4 shadow-lg ${colors} flex items-center gap-2`}>
            {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message}
        </div>
    );
}

export default function ProjectsPage() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notif, setNotif] = useState(null);
    const [editing, setEditing] = useState(null); // project id or 'new'
    const [form, setForm] = useState({});

    const fetchProjects = () => {
        api.get('/projects').then(({ data }) => setProjects(data)).finally(() => setLoading(false));
    };

    useEffect(() => { fetchProjects(); }, []);

    const blankForm = {
        name: '', description: '', tech_stack: [], url: '', github_url: '',
        highlights: [], display_order: projects.length, is_featured: false,
    };

    const openNew = () => {
        setForm({ ...blankForm, display_order: projects.length });
        setEditing('new');
    };

    const openEdit = (p) => {
        setForm({
            name: p.name || '',
            description: p.description || '',
            tech_stack: p.tech_stack || [],
            url: p.url || '',
            github_url: p.github_url || '',
            highlights: p.highlights || [],
            display_order: p.display_order || 0,
            is_featured: p.is_featured || false,
        });
        setEditing(p.id);
    };

    const handleSave = async () => {
        try {
            const payload = {
                ...form,
                tech_stack: typeof form.tech_stack === 'string'
                    ? form.tech_stack.split(',').map((s) => s.trim()).filter(Boolean)
                    : form.tech_stack,
                highlights: typeof form.highlights === 'string'
                    ? form.highlights.split('\n').map((s) => s.trim()).filter(Boolean)
                    : form.highlights,
            };
            if (editing === 'new') {
                await api.post('/projects', payload);
                setNotif({ type: 'success', message: 'Projet créé !' });
            } else {
                await api.put(`/projects/${editing}`, payload);
                setNotif({ type: 'success', message: 'Projet mis à jour !' });
            }
            setEditing(null);
            fetchProjects();
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce projet ?')) return;
        try {
            await api.delete(`/projects/${id}`);
            setNotif({ type: 'success', message: 'Projet supprimé' });
            fetchProjects();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur de suppression' });
        }
    };

    const handleScreenshot = async (projectId, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        try {
            await api.post(`/projects/${projectId}/screenshots`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setNotif({ type: 'success', message: 'Screenshot ajouté !' });
            fetchProjects();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur upload screenshot' });
        }
    };

    const deleteScreenshot = async (projectId, screenshotId) => {
        try {
            await api.delete(`/projects/${projectId}/screenshots/${screenshotId}`);
            fetchProjects();
        } catch (err) {
            setNotif({ type: 'error', message: 'Erreur suppression screenshot' });
        }
    };

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    return (
        <div>
            {notif && <Notification {...notif} onClose={() => setNotif(null)} />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Projets</h1>
                <button onClick={openNew} className="flex items-center gap-2 bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    <Plus className="w-4 h-4" />
                    Nouveau projet
                </button>
            </div>

            {/* Edit form */}
            {editing !== null && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {editing === 'new' ? 'Nouveau projet' : 'Modifier le projet'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                            <input value={form.url || ''} onChange={(e) => setForm({ ...form, url: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">GitHub URL</label>
                            <input value={form.github_url || ''} onChange={(e) => setForm({ ...form, github_url: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ordre d'affichage</label>
                            <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm" />
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Technologies (séparées par des virgules)</label>
                        <input value={Array.isArray(form.tech_stack) ? form.tech_stack.join(', ') : form.tech_stack}
                            onChange={(e) => setForm({ ...form, tech_stack: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                            placeholder="Python, React, Docker" />
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Points forts (un par ligne)</label>
                        <textarea value={Array.isArray(form.highlights) ? form.highlights.join('\n') : form.highlights}
                            onChange={(e) => setForm({ ...form, highlights: e.target.value })} rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                            placeholder="Architecture multi-tenant&#10;Pipeline RAG complet" />
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                        <input type="checkbox" id="featured" checked={form.is_featured}
                            onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                            className="rounded border-gray-300 text-[#5764F4] focus:ring-[#5764F4]" />
                        <label htmlFor="featured" className="text-sm text-gray-700">Projet mis en avant</label>
                    </div>
                    <div className="mt-6 flex gap-3">
                        <button onClick={handleSave} className="bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors">
                            Sauvegarder
                        </button>
                        <button onClick={() => setEditing(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors">
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            {/* Project list */}
            <div className="space-y-4">
                {projects.length === 0 && !editing && (
                    <p className="text-center text-gray-500 py-12">Aucun projet. Commencez par en créer un !</p>
                )}
                {projects.map((p) => (
                    <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold text-gray-900">{p.name}</h3>
                                    {p.is_featured && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">⭐ Featured</span>}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{p.description}</p>
                                {p.tech_stack?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {p.tech_stack.map((t) => (
                                            <span key={t} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{t}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                                <button onClick={() => openEdit(p)} className="p-2 text-gray-400 hover:text-[#5764F4] hover:bg-indigo-50 rounded-lg transition-colors">
                                    <Edit3 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(p.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Screenshots */}
                        <div className="mt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Image className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-500">Screenshots ({p.screenshots?.length || 0})</span>
                                <label className="text-sm text-[#5764F4] hover:underline cursor-pointer">
                                    + Ajouter
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleScreenshot(p.id, e)} />
                                </label>
                            </div>
                            {p.screenshots?.length > 0 && (
                                <div className="flex gap-3 flex-wrap">
                                    {p.screenshots.map((s) => (
                                        <div key={s.id} className="relative group">
                                            <img src={`${API_BASE}${s.image_url}`} alt={s.caption || p.name}
                                                className="w-32 h-20 object-cover rounded-lg border border-gray-200" />
                                            <button onClick={() => deleteScreenshot(p.id, s.id)}
                                                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
